import React from 'react';
import { Box, Chip, Skeleton, Typography } from '@mui/material';
import { Activity, AlertTriangle, Clock, Cloud, Coins } from 'lucide-react';

import type { AiLogSummary } from '../../../types/aiLog';
import { formatCredits, formatLatency, formatUsd } from './aiLogFormat';

type SummaryCardProps = {
  title: string;
  value: string;
  subtitle?: string;
  tone?: 'default' | 'danger';
  icon: React.ReactNode;
  chip?: React.ReactNode;
  loading?: boolean;
};

const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  value,
  subtitle,
  tone = 'default',
  icon,
  chip,
  loading,
}) => (
  <Box className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
    <Box className="mb-3 flex items-start justify-between gap-3">
      <Box>
        <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
          {title}
        </Typography>
        {loading ? (
          <Skeleton width={96} height={36} />
        ) : (
          <Typography
            variant="h5"
            className={
              tone === 'danger'
                ? 'font-semibold text-red-600 dark:text-red-400'
                : 'font-semibold text-gray-900 dark:text-slate-100'
            }
          >
            {value}
          </Typography>
        )}
      </Box>
      <Box className="rounded-md bg-slate-100 p-2 text-slate-500 dark:bg-slate-700 dark:text-slate-300">
        {icon}
      </Box>
    </Box>
    <Box className="flex min-h-6 items-center gap-2">
      {subtitle && (
        <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
          {subtitle}
        </Typography>
      )}
      {chip}
    </Box>
  </Box>
);

type AiLogsSummaryCardsProps = {
  summary: AiLogSummary | undefined;
  loading: boolean;
};

export const AiLogsSummaryCards: React.FC<AiLogsSummaryCardsProps> = ({ summary, loading }) => {
  const outOfTolerance = summary?.outOfToleranceCount ?? null;

  return (
    <Box className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
      <SummaryCard
        title="Calls"
        value={
          summary?.callCount === null || summary?.callCount === undefined
            ? '—'
            : `${summary.callCount}`
        }
        icon={<Activity size={20} />}
        chip={summary?.sampled ? <Chip size="small" label="sampled" color="warning" /> : undefined}
        loading={loading}
      />
      <SummaryCard
        title="Credits charged"
        value={formatCredits(summary?.totalShadowCredits)}
        icon={<Coins size={20} />}
        loading={loading}
      />
      <SummaryCard
        title="Cloudflare cost"
        value={formatUsd(summary?.totalGatewayCostUsd)}
        subtitle={`${summary?.unreconciledCount ?? 0} awaiting Cloudflare cost`}
        icon={<Cloud size={20} />}
        loading={loading}
      />
      <SummaryCard
        title="Out of tolerance"
        value={outOfTolerance === null ? '—' : `${outOfTolerance}`}
        tone={outOfTolerance && outOfTolerance > 0 ? 'danger' : 'default'}
        icon={<AlertTriangle size={20} />}
        loading={loading}
      />
      <SummaryCard
        title="Latency"
        value={`avg ${formatLatency(summary?.avgLatencyMs)}`}
        subtitle={`p95 ${formatLatency(summary?.p95LatencyMs)}`}
        icon={<Clock size={20} />}
        loading={loading}
      />
    </Box>
  );
};
